import './manage-user.scss'

import { format } from 'date-fns'
import { useState } from 'react'

import { Space, Tabs, Typography } from '../../../components/core-v2'
import { UserType } from '../../../types/user'
import { UserState } from '../common/users.state'
import { EditUser } from './edit-user'
import UserOperationWithConfirm from './user-operation-with-confirm'

type TabId = 'edit' | 'delete' | 'unlock' | 'activate'

export type Props = {
  initialTabId?: TabId
  notifyParentOfSave: () => void
  onActivate: () => void
  onDelete: () => void
  onUnlock: () => void
  operation: 'list' | 'edit'
  user: UserState
  userType: UserType
}

const getLastLoginDate = (user: UserState) => {
  if (!user.lastLoginDate) {
    return 'n/a'
  }

  try {
    const date = new Date(user.lastLoginDate)
    return format(date, 'dd/MM/yyyy hh:mm')
  } catch {
    return 'n/a'
  }
}

export function ManageUser({
  initialTabId = 'edit',
  notifyParentOfSave,
  onActivate,
  onDelete,
  onUnlock,
  operation,
  user,
  userType
}: Props) {
  const [activeTabId, setActiveTabId] = useState<TabId>(initialTabId)

  return (
    <div className="manage-block">
      <div className="details">
        <span>
          Last login: <strong>{getLastLoginDate(user)}</strong>
        </span>
        <span>
          ID: <strong>{user.userId}</strong>
        </span>
      </div>
      <Tabs.List ruleClassName="mb-4" spacing="sm">
        <Tabs.Tab
          isSelected={activeTabId === 'edit'}
          value="edit"
          onClick={() => setActiveTabId('edit')}
        >
          Edit user
        </Tabs.Tab>
        {user.status === 'Locked' && (
          <Tabs.Tab
            isSelected={activeTabId === 'unlock'}
            value="unlock"
            onClick={() => setActiveTabId('unlock')}
          >
            Unlock user
          </Tabs.Tab>
        )}
        {user.status === 'Inactive' && (
          <Tabs.Tab
            isSelected={activeTabId === 'activate'}
            value="activate"
            onClick={() => setActiveTabId('activate')}
          >
            Activate user
          </Tabs.Tab>
        )}
        <Tabs.Tab
          isSelected={activeTabId === 'delete'}
          value="delete"
          onClick={() => setActiveTabId('delete')}
        >
          Delete user
        </Tabs.Tab>
      </Tabs.List>
      {operation === 'edit' && activeTabId === 'edit' && (
        <Tabs.Panel value="edit">
          <EditUser notifyParentOfSave={notifyParentOfSave} user={user} userType={userType} />
        </Tabs.Panel>
      )}
      {operation === 'edit' && activeTabId === 'delete' && (
        <Tabs.Panel value="delete">
          <Space mb={5}>
            <Typography fontSize="13px" fontWeight="800" variant="b3">
              Delete this user
            </Typography>
          </Space>
          <UserOperationWithConfirm
            operationTitle="Delete"
            operationType="destructive"
            userName={user.userName}
            onConfirm={onDelete}
          />
        </Tabs.Panel>
      )}
      {operation === 'edit' && activeTabId === 'unlock' && (
        <Tabs.Panel value="unlock">
          <Space mb={5}>
            <Typography fontSize="13px" fontWeight="800" variant="b3">
              Unlock this user
            </Typography>
          </Space>
          <UserOperationWithConfirm
            operationTitle="Unlock"
            operationType="non-destructive"
            userName={user.userName}
            onConfirm={onUnlock}
          />
        </Tabs.Panel>
      )}
      {operation === 'edit' && activeTabId === 'activate' && (
        <Tabs.Panel value="activate">
          <Space mb={5}>
            <Typography fontSize="13px" fontWeight="800" variant="b3">
              Unlock this user
            </Typography>
          </Space>
          <UserOperationWithConfirm
            operationTitle="Activate"
            operationType="non-destructive"
            userName={user.userName}
            onConfirm={onActivate}
          />
        </Tabs.Panel>
      )}
    </div>
  )
}
